/**
 * WeekView component for calendar
 * Displays events in a weekly timeline layout
 */

'use client';

import { darkenHexColorWithOpacity } from '@/utils/colorService';
import { Clock, MapPin } from 'lucide-react';
import Link from 'next/link';
import { useMemo } from 'react';
import type { EventTeaser } from '../../types';

interface WeekViewProps {
  events: EventTeaser[];
  currentDate: Date;
  baseUrl?: string;
}

interface DayColumn {
  date: Date;
  isToday: boolean;
  events: EventTeaser[];
}

export function WeekView({ events, currentDate, baseUrl = '/events' }: WeekViewProps) {
  // Build week columns
  const weekDays = useMemo(() => {
    const weekStart = new Date(currentDate);
    weekStart.setDate(currentDate.getDate() - currentDate.getDay()); // Start on Sunday
    weekStart.setHours(0, 0, 0, 0);

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const days: DayColumn[] = [];

    for (let i = 0; i < 7; i++) {
      const dayDate = new Date(weekStart);
      dayDate.setDate(weekStart.getDate() + i);

      // Find events for this day
      const dayEvents = events.filter(event => {
        if (!event.eventDateTimeRange?.start?.timestamp) return false;

        const eventStart = new Date(event.eventDateTimeRange.start.timestamp * 1000);
        eventStart.setHours(0, 0, 0, 0);

        const eventEnd = event.eventDateTimeRange.end?.timestamp
          ? new Date(event.eventDateTimeRange.end.timestamp * 1000)
          : new Date(eventStart);
        eventEnd.setHours(23, 59, 59, 999);

        return dayDate >= eventStart && dayDate <= eventEnd;
      });

      // Sort events by time
      dayEvents.sort((a, b) => {
        const timeA = a.eventDateTimeRange?.start?.timestamp ?? 0;
        const timeB = b.eventDateTimeRange?.start?.timestamp ?? 0;
        return timeA - timeB;
      });

      days.push({
        date: dayDate,
        isToday: dayDate.getTime() === today.getTime(),
        events: dayEvents,
      });
    }

    return days;
  }, [currentDate, events]);

  return (
    <div className="overflow-x-auto">
      {/* Day headers */}
      <div className="grid grid-cols-7 gap-px bg-border">
        {weekDays.map((day, index) => (
          <div
            key={index}
            className={`bg-card p-3 text-centre ${day.isToday ? 'bg-primary/10' : ''}`}
          >
            <div className="text-xs font-medium text-muted-foreground">
              {day.date.toLocaleDateString('en-GB', { weekday: 'short' })}
            </div>
            <div className={`mt-1 text-lg font-semibold ${day.isToday ? 'text-primary' : ''}`}>
              {day.date.getDate()}
            </div>
          </div>
        ))}
      </div>

      {/* Events grid */}
      <div className="grid grid-cols-7 gap-px bg-border">
        {weekDays.map((day, index) => (
          <div
            key={index}
            className={`min-h-[400px] bg-card p-2 ${day.isToday ? 'bg-primary/5' : ''}`}
          >
            <div className="space-y-2">
              {day.events.map(event => {
                const startTime = event.eventDateTimeRange?.start?.timestamp
                  ? new Date(event.eventDateTimeRange.start.timestamp * 1000).toLocaleTimeString(
                      'en-GB',
                      {
                        hour: '2-digit',
                        minute: '2-digit',
                      }
                    )
                  : '';

                // event.path already contains full path like /events/title
                const eventLink = event.path;

                if (!eventLink) return null;

                // Extract eventTypeColor from eventType
                const eventTypeObj = event.eventType as
                  | { eventTypeColor?: string | null }
                  | null
                  | undefined;
                const eventTypeColor = eventTypeObj?.eventTypeColor ?? undefined;

                // Normalize eventType for display
                const normalizedTypes = event.eventType
                  ? Array.isArray(event.eventType)
                    ? event.eventType
                    : [event.eventType]
                  : [];
                const firstType = normalizedTypes[0];

                return (
                  <Link
                    key={event.id}
                    href={eventLink}
                    className={`group block rounded-lg border p-2 transition-colours hover:border-primary hover:shadow-sm ${
                      eventTypeColor ? '' : 'bg-background'
                    }`}
                    style={{
                      backgroundColor: eventTypeColor
                        ? darkenHexColorWithOpacity(eventTypeColor, 80, 0.7)
                        : undefined,
                    }}
                  >
                    {startTime && (
                      <div className="mb-1 flex items-centre gap-1 text-xs text-muted-foreground">
                        <Clock className="h-3 w-3" style={{ color: eventTypeColor || undefined }} />
                        <span className="font-medium">{startTime}</span>
                      </div>
                    )}
                    <h4 className="mb-1 line-clamp-2 text-sm font-semibold leading-tight group-hover:text-primary">
                      {event.title}
                    </h4>
                    {event.eventLocation && (
                      <div className="flex items-centre gap-1 text-xs text-muted-foreground">
                        <MapPin className="h-3 w-3 flex-shrink-0" />
                        <span className="truncate">{event.eventLocation}</span>
                      </div>
                    )}
                    {firstType && (
                      <div className="mt-1">
                        <span
                          className="inline-block rounded px-1.5 py-0.5 text-[10px] font-medium"
                          style={{
                            backgroundColor: eventTypeColor
                              ? `${eventTypeColor}20`
                              : 'hsl(var(--primary) / 0.1)',
                            color: eventTypeColor || 'hsl(var(--primary))',
                          }}
                        >
                          {firstType.name}
                        </span>
                      </div>
                    )}
                  </Link>
                );
              })}
              {day.events.length === 0 && (
                <div className="flex h-full min-h-[100px] items-centre justify-centre text-xs text-muted-foreground">
                  No events
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
